import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { Place } from './place';
import { Session } from './session';
import { Ticket } from './ticket';
import { CashChange } from './cashchange';
import { AuditInfo } from './auditinfo';
import { AuditTill } from './audittill';
import { PlaceService } from './svcplace';

export interface _Audit {
    place: number;
    session: number;
    cash: number;
};

interface _AuditData extends _Audit {
    objid?: number;
    _uuid?: string;
    created?: Date;
};

abstract class AuditData extends DataObject {
    protected _audit: _AuditData = {
        place: null,
        session: null,
        cash: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
        this._audit.created = new Date();
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._audit.created;
    }

    get place(): Place{
        return this._children['place'] || null;
    }

    set place(value: Place){
        if (this.SetChild('place', value, 'place')){
            this.ToUpdate = true;
        }
    }

    get session(): Session{
        return this._children['session'] || null;
    }

    set session(value: Session){
        if (this.SetChild('session', value, 'session')){
            this.ToUpdate = true;
        }
    }

    get cashchanges(): Array<CashChange> {
        return this._chldlist['cashchange'] || [];
    }

    get cash(): number{
        return this._audit.cash;
    }

    set cash(value: number){
        if (this.patchValue(this._audit, 'cash', value)){
            this.ToUpdate = true;
        }
    }

    get auditinfo(): AuditInfo {
        let _auditinfos = this._chldlist['auditinfo'] || [];
        if (_auditinfos.length > 0){
            return _auditinfos[0]
        }
    
        return null;    // no audit information for this audit?
    }

    protected get tills(): Array <AuditTill> {
        return this._chldlist['till'] || [];
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change(){
        return {
            place: this._audit.place,
            session: this._audit.session,
            cash: this._audit.cash
        };
    }

    protected get Depend() {
        return {
            place: { item: this.place, relation_info: { to: 'audits', by: 'place' } },  // this[by -> 'place'][to -> 'audits'] => this
        };
    }

    protected get tickets() : Array <Ticket> {
        return [];      // implemented on derived class
    }

    protected get Children() {
        let _children = [];

        for(let _item of this.cashchanges){
            _children.push(_item);
        }

        for(let _item of this.tickets){
            _children.push(_item);
        }

        /*  audittill entries must be sent prior to auditinfo entries */

        for(let _item of this.tills){
            _children.push(_item);
        }

        if (this.auditinfo){
            _children.push(this.auditinfo);
        }

        return _children;
    }
    
    /****************************/
    /* DATA OBJECT              */
    /****************************/

    private _patchData(_audit: _Audit){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._audit, 'place', _audit['place']) || _toUpdate;
        _toUpdate = this.patchValue(this._audit, 'session', _audit['session']) || _toUpdate;
        _toUpdate = this.patchValue(this._audit, 'cash', _audit['cash']) || _toUpdate;

        return _toUpdate;
    }
    
    set Data(_audit: _Audit){
        this.patchValue(this._audit, 'created', _audit['created']);
        
        if (this._patchData(_audit)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._audit;
    }

    set Info(value){
        this.DoPatchValues(value);
    }

    private _ddbb(info): _AuditData {
        let _audit: _AuditData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            place: info['place'] ? parseInt(info['place']) : null,
            session: info['session'] ? parseInt(info['session']) : null,
            cash: info['cash'] ? parseFloat(info['cash']) : 0
        };
        return _audit;
    }

    private DoPatchValues(_audit: _Audit){
        this._patchData(_audit);

        if (_audit['session']){     // update children: 'session'
            let _objid = _audit['session'].toString();
            this.SetChild('session', new Session(_objid, this.data), 'session');
        }
        else {
            this.SetChild('session', null, 'session');
        }

        if (_audit['place']){     // update children: 'place'
            let _objid = _audit['place'].toString();
            this.SetChild('place', new Place(_objid, this.data, this._objoptions), 'place');
        }  
        else {
            this.SetChild('place', null, 'place');
        }
    }

    protected _OnUpdate(info){
        let _audit = this._ddbb(info);
        this.patchValue(this._audit, 'objid', _audit['objid']);
        this.patchValue(this._audit, 'created', _audit['created']);
        this.DoPatchValues(_audit);

        if ('cashchange' in info) {  // update children: 'cashchange'
            this.SetChildren <CashChange> (info['cashchange'], 'cashchange', CashChange, 'audit');
        }

        if ('till' in info) {  // update children: 'till'
            this.SetChildren <AuditTill> (info['till'], 'till', AuditTill, 'audit');
        }        

        if ('auditinfo' in info) {  // update children: 'auditinfo'
            this.SetChildren <AuditInfo> (info['auditinfo'], 'auditinfo', AuditInfo, 'audit');
        }
    }
}

export class Audit extends AuditData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('AUDIT', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): Audit {
        return this._Copy(store) as Audit;
    }

    /****************************/
    /* CUSTOM MEMBERS           */
    /****************************/

    get till(): PlaceService {
        let _tills = this.tills;
        if (_tills.length > 0){
            return _tills[0].till;
        }
        return null;
    }

    set till(value: PlaceService){
        if (value == null){
            console.warn("[AUDIT] Cannot set an invalid till to this audit! Operation ignored");
            return;
        }

        let _till = this.till;
        if (_till){
            console.warn("[AUDIT] Cannot change this audit's till! Operation ignored");
        }
        else {
            let _audittill = new AuditTill(null, this.data);
            if (_audittill){
                _audittill.audit = this;
                _audittill.till = value;
                _audittill.status = 'AC';
            }

            this.AddChild('till', _audittill, 'audit');
        }
    }

    /****************************/
    /* CHILDREN MANAGEMENT      */
    /****************************/
    
    protected AddTicket(child: Ticket){
        if (child){
            child.audit = this;     // assing the audit to the ticket

            if (!('ticket' in this._children)){
                this._children['ticket'] = new Set <Ticket> ();
            }

            this._children['ticket'].add(child);
        }
    }

    /****************************/
    /* CHILD ACCESS             */
    /****************************/
    
    protected get tickets() : Array <Ticket> {
        if ('ticket' in this._children){
            return [...this._children['ticket']];
        }

        return [];
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(){
        return this.IsLoaded;
    }

    get IsCommited(){
        return (this.ToInsert == false) && (this.objid != null);
    }

    InTill(till){
        if (this.place.MultiplePOS){
            return this.till == till;
        }
        return true;    // single POS
    }

    get Previous() : Audit {
        let _audits = this.place.audits.slice(0);
        let _intill = [];
        for (let _audit of _audits){
            if (this.InTill){
                _intill.push(_audit);
            }
        }

        _audits = _intill.sort((a, b) => {
            return a.created.getTime() - b.created.getTime();
        });

        if (_audits.length > 0){
            let _idx = _audits.indexOf(this);
            if (_idx < 0){    // not found (get the last one)
                return _audits[_audits.length-1];
            }
    
            if (_idx > 0){    // found (get the previous one)
                return _audits[_idx-1];
            }    
        }

        return null;
    }

    private get _AuditTickets() {
        return new Promise(async (resolve) => {
            let _tickets = [];

            let _prev = this.Previous?.created || null;     // audit ini date
            let _next = this.created;                       // audit end date
    
            // obtain the ticket information for the given audit period
            let _placetickets = await this.data.LoadPlaceTickets(this.place, _prev, _next, true);
            for(let _ticket of _placetickets){
                if (!_ticket.InAudit(_prev, _next, this)){
                    continue;   // this ticket does not belog to this audit
                }

                _tickets.push(_ticket);
            }
    
            resolve(_tickets);    
        });
    }

    async ReloadInfo(){
        if (this.auditinfo == null){
            let _auditinfo = new AuditInfo(null, this.data);
            if (_auditinfo){
                _auditinfo.audit = this;
            }

            this.AddChild('auditinfo', _auditinfo, 'audit');
        }

        let _tickets = await this._AuditTickets as Array <Ticket>;
        if (this.auditinfo != null){
            this.auditinfo.Reload(_tickets);

            if (this.place.MultiplePOS){
                for(let _ticket of _tickets){
                    this.AddTicket(_ticket);    // will apply on commit
                }    
            }
        }

        this.auditinfo.DoRefresh('AUDITINFO');    
    }
}

